import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { gameApi } from "@/lib/api";
import EnigmeModal from "@/components/game/EnigmeModal";
import CarnetVirtuel from "@/components/game/CarnetVirtuel";
import CarteJeu from "@/components/game/CarteJeu";
import SageChat from "@/components/game/SageChat";
import {
  Compass,
  Clock,
  Trophy,
  KeyRound,
  Lock,
  Unlock,
  MapPin,
  Sparkles,
  ArrowLeft,
} from "lucide-react";

/* -----------------------------------------------------------
   JouerGame — Plateau de jeu principal
   /jouer/:code  →  liste des étapes + énigmes, modale, carnet
   ----------------------------------------------------------- */

function formatTime(s) {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${h}h ${m.toString().padStart(2, "0")}m`
    : `${m}m ${sec.toString().padStart(2, "0")}s`;
}

export default function JouerGame() {
  const { code } = useParams();
  const codeUpper = (code || "").toUpperCase();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeEnigme, setActiveEnigme] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const load = useCallback(async () => {
    try {
      const s = await gameApi.byCode(codeUpper);
      setSession(s);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Session introuvable");
    } finally {
      setLoading(false);
    }
  }, [codeUpper]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-démarrer si EN_ATTENTE
  useEffect(() => {
    if (session && session.statut === "EN_ATTENTE") {
      gameApi.demarrer(codeUpper).then(() => load());
    }
  }, [session, codeUpper, load]);

  // Timer
  useEffect(() => {
    if (!session?.debutAt || session.statut !== "EN_COURS") return;
    const debut = new Date(session.debutAt).getTime();
    setElapsed(Math.floor((Date.now() - debut) / 1000));
    const t = setInterval(() => {
      setElapsed(Math.floor((Date.now() - debut) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [session?.debutAt, session?.statut]);

  const cadenasOuverts = useMemo(() => {
    const set = new Set();
    session?.codesOuverts?.forEach((c) => set.add(c.numeroCadenas));
    return set;
  }, [session]);

  const totalEnigmes = useMemo(
    () =>
      session?.parcours?.etapes?.reduce(
        (s, e) => s + (e.enigmes?.length || 0),
        0
      ) || 0,
    [session]
  );

  if (loading) {
    return <FullScreen msg="Chargement de votre aventure…" />;
  }
  if (error || !session) {
    return (
      <FullScreen
        msg={error || "Session introuvable"}
        error
        link="/jouer"
        linkLabel="Retour à l'entrée"
      />
    );
  }

  const nbOuverts = cadenasOuverts.size;
  const progression = totalEnigmes
    ? Math.round((nbOuverts / totalEnigmes) * 100)
    : 0;
  const allDone = totalEnigmes > 0 && nbOuverts >= totalEnigmes;

  return (
    <div
      className="min-h-screen bg-noir text-parchemin bg-grain pb-24"
      data-testid="jouer-game"
    >
      {/* Header sticky */}
      <header className="sticky top-0 z-30 bg-noir-100/95 backdrop-blur-md border-b border-or/20">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Compass className="w-4 h-4 text-or flex-shrink-0" />
              <div className="min-w-0">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-or/70 flex items-center gap-1.5">
                  <MapPin className="w-3 h-3" />
                  <span className="truncate">
                    {session.parcours.ville} · {session.parcours.nom}
                  </span>
                </div>
                <div
                  className="font-display text-parchemin text-base leading-tight truncate"
                  data-testid="groupe-nom"
                >
                  {session.groupeNom || "Équipe"}
                  <span className="text-parchemin/50 text-xs font-mono ml-2">
                    · {session.nbJoueurs}&nbsp;joueur{session.nbJoueurs > 1 ? "s" : ""}
                  </span>
                </div>
              </div>
            </div>
            <Link
              to="/jouer"
              className="text-parchemin/40 hover:text-or transition p-1.5"
              aria-label="Quitter"
              data-testid="game-back"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
          </div>

          {/* Stats */}
          <div className="mt-3 grid grid-cols-3 gap-2">
            <Stat
              icon={<Trophy className="w-3 h-3" />}
              label="Score"
              value={session.score || 0}
              testId="score-display"
            />
            <Stat
              icon={<Clock className="w-3 h-3" />}
              label="Temps"
              value={formatTime(elapsed)}
              testId="timer-display"
              accent="vert"
            />
            <Stat
              icon={<KeyRound className="w-3 h-3" />}
              label="Cadenas"
              value={`${nbOuverts}/${totalEnigmes}`}
              testId="cadenas-counter"
            />
          </div>

          {/* Progress bar */}
          <div className="mt-3 h-1.5 bg-noir rounded-full overflow-hidden">
            <div
              data-testid="progress-bar"
              className="h-full bg-gradient-to-r from-or to-vert-50 transition-all duration-700"
              style={{ width: `${progression}%` }}
            />
          </div>
        </div>
      </header>

      {/* Étapes */}
      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {allDone && (
          <div
            className="p-5 border-2 border-or bg-gradient-to-br from-vert-900/50 to-noir rounded-sm text-center"
            data-testid="game-victory-banner"
          >
            <Sparkles className="w-6 h-6 text-or mx-auto mb-2 animate-glow-pulse" />
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-or">
              Aventure achevée
            </div>
            <div className="mt-2 font-display text-2xl text-parchemin">
              Bravo <span className="italic text-or">{session.groupeNom}</span>&nbsp;!
            </div>
            <p className="mt-2 text-sm text-parchemin/70">
              {totalEnigmes} énigmes résolues · {session.score} points ·{" "}
              {formatTime(elapsed)}
            </p>
          </div>
        )}

        {session.parcours.etapes.map((etape) => (
          <EtapeBlock
            key={etape.id}
            etape={etape}
            cadenasOuverts={cadenasOuverts}
            onOpenEnigme={(en) => setActiveEnigme({ enigme: en, etape })}
          />
        ))}
      </main>

      {/* Modal énigme */}
      {activeEnigme && (
        <EnigmeModal
          enigme={activeEnigme.enigme}
          etape={activeEnigme.etape}
          session={session}
          onClose={() => setActiveEnigme(null)}
          onSolved={() => {
            setActiveEnigme(null);
            load();
          }}
        />
      )}

      {/* Carnet */}
      <CarnetVirtuel
        etapes={session.parcours.etapes}
        cadenasOuverts={cadenasOuverts}
        codesOuverts={session.codesOuverts || []}
      />

      {/* Carte interactive */}
      <CarteJeu
        etapes={session.parcours.etapes}
        cadenasOuverts={cadenasOuverts}
        pistesActives={session.pistesActives || []}
      />

      {/* Le Sage du Causse — indices contre énigmes IA */}
      <SageChat
        code={codeUpper}
        chanceCardsUsed={session.chanceCardsUsed || 0}
        chanceCardsMax={session.chanceCardsMax || 3}
        sageSession={session.sageSession || null}
        onUpdated={load}
      />
    </div>
  );
}

/* --- Stat pill --- */
function Stat({ icon, label, value, testId, accent }) {
  const color =
    accent === "vert"
      ? "text-vert-50 border-vert-50/30"
      : "text-or border-or/30";
  return (
    <div className={`p-2.5 rounded-sm border bg-noir-50/40 ${color}`}>
      <div className="flex items-center gap-1 font-mono text-[9px] tracking-[0.2em] uppercase text-parchemin/50 mb-0.5">
        {icon}
        {label}
      </div>
      <div
        data-testid={testId}
        className={`font-display text-lg leading-none stat-graved ${color.split(" ")[0]}`}
      >
        {value}
      </div>
    </div>
  );
}

/* --- Bloc étape --- */
function EtapeBlock({ etape, cadenasOuverts, onOpenEnigme }) {
  const allOpen =
    etape.enigmes?.every((e) => cadenasOuverts.has(e.numeroCadenas)) || false;

  return (
    <section
      data-testid={`etape-${etape.ordre}`}
      className={`border rounded-sm p-4 md:p-5 transition ${
        allOpen
          ? "bg-vert-900/30 border-vert-50/40"
          : "bg-noir-50/50 border-or/15"
      }`}
    >
      {/* Titre étape */}
      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 rounded-sm border border-or/30 bg-noir flex items-center justify-center text-2xl leading-none flex-shrink-0">
          {etape.emoji || "📍"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-or/80">
            Étape {etape.ordre}
          </div>
          <h3 className="font-display text-lg md:text-xl text-parchemin leading-tight">
            {etape.nom}
          </h3>
          <div className="mt-1 font-mono text-[11px] text-parchemin/45">
            {etape.enigmes?.length || 0}&nbsp;énigme
            {etape.enigmes?.length > 1 ? "s" : ""}
          </div>
        </div>
        {allOpen && (
          <span
            className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-vert-900 border border-vert-50/50 text-vert-50 text-[10px] font-mono uppercase tracking-widest flex-shrink-0"
            data-testid={`etape-${etape.ordre}-done`}
          >
            <Sparkles className="w-3 h-3" />
            Complète
          </span>
        )}
      </div>

      {/* Énigmes */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {etape.enigmes?.map((eg) => {
          const ouvert = cadenasOuverts.has(eg.numeroCadenas);
          return (
            <button
              key={eg.id}
              onClick={() => !ouvert && onOpenEnigme(eg)}
              disabled={ouvert}
              data-testid={`enigme-card-${eg.numeroCadenas}`}
              className={`group p-3 rounded-sm border-2 transition-all ${
                ouvert
                  ? "bg-vert-900/50 border-vert-50/60 cursor-default"
                  : "bg-noir border-or/20 hover:border-or hover:bg-or/5 active:scale-95"
              }`}
            >
              <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-parchemin/50 mb-2">
                Cadenas #{eg.numeroCadenas}
              </div>
              {ouvert ? (
                <>
                  <Unlock
                    className="w-6 h-6 text-vert-50 mx-auto mb-1"
                    strokeWidth={1.5}
                  />
                  <div className="font-mono font-bold text-lg text-vert-50 tracking-[0.2em] stat-graved">
                    {eg.codeCadenas}
                  </div>
                </>
              ) : (
                <>
                  <Lock
                    className="w-6 h-6 text-or mx-auto mb-1 group-hover:scale-110 transition-transform"
                    strokeWidth={1.5}
                  />
                  <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-or/70">
                    {eg.type}
                  </div>
                </>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}

/* --- Écran plein pour loading / erreur --- */
function FullScreen({ msg, error, link, linkLabel }) {
  return (
    <div
      className="min-h-screen bg-noir text-parchemin flex flex-col items-center justify-center px-6 gap-4"
      data-testid="jouer-fullscreen"
    >
      <Compass
        className={`w-8 h-8 text-or ${!error ? "animate-glow-pulse" : ""}`}
      />
      <p className="text-sm font-mono uppercase tracking-widest text-parchemin/60">
        {msg}
      </p>
      {link && (
        <Link
          to={link}
          className="mt-2 inline-flex items-center gap-2 px-4 py-2 border border-or/30 hover:border-or hover:bg-or/5 text-or rounded-sm text-xs font-mono uppercase tracking-widest transition"
          data-testid="fullscreen-link"
        >
          <ArrowLeft className="w-3 h-3" />
          {linkLabel || "Retour"}
        </Link>
      )}
    </div>
  );
}
